// src/pages/rapim/RapimCalendar.tsx — MODERN REDESIGN
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Calendar, List, ChevronRight, ChevronLeft, Clock,
  MapPin, FileText, Users, Gavel, Search,
  CalendarDays, ArrowUpRight, X,
  CheckCircle, XCircle, Play
} from 'lucide-react';
import { Button, Card, Breadcrumb, Loading } from '../../components/ui';
import { RapatPimpinan } from '../../types';
import { rapimService } from '../../services/rapim';
import { useAuthStore } from '../../store/authStore';
import { useCapabilities } from '../../hooks/useCapabilities';
import toast from 'react-hot-toast';

// ========== CONSTANTS ==========
const STATUS_CONFIG: Record<string, {
  label: string; icon: React.ReactNode; color: string;
  bg: string; border: string; dot: string; gradient: string;
}> = {
  dijadwalkan: {
    label: 'Dijadwalkan', icon: <Calendar className="w-4 h-4" />,
    color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200',
    dot: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600'
  },
  berlangsung: {
    label: 'Berlangsung', icon: <Play className="w-4 h-4" />,
    color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200',
    dot: 'bg-amber-500', gradient: 'from-amber-500 to-amber-600'
  },
  selesai: {
    label: 'Selesai', icon: <CheckCircle className="w-4 h-4" />,
    color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200',
    dot: 'bg-emerald-500', gradient: 'from-emerald-500 to-emerald-600'
  },
  dibatalkan: {
    label: 'Dibatalkan', icon: <XCircle className="w-4 h-4" />,
    color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200',
    dot: 'bg-red-500', gradient: 'from-red-500 to-red-600'
  }
};

const JENIS_CONFIG: Record<string, { label: string; color: string; emoji: string }> = {
  rutin: { label: 'Rutin', color: 'bg-teal-500', emoji: '📅' },
  insidental: { label: 'Insidental', color: 'bg-amber-500', emoji: '⚡' },
  darurat: { label: 'Darurat', color: 'bg-red-500', emoji: '🚨' }
};

const NAMA_BULAN = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];
const NAMA_HARI = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

type StatusTab = 'semua' | 'dijadwalkan' | 'berlangsung' | 'selesai' | 'dibatalkan';
type ViewMode = 'list' | 'calendar';

// ========== SUB-COMPONENTS ==========

// Stat Card dengan gradient modern
const StatCard: React.FC<{
  icon: React.ReactNode; label: string; value: number;
  gradient: string; active?: boolean; onClick?: () => void;
}> = ({ icon, label, value, gradient, active, onClick }) => (
  <button
    onClick={onClick}
    className={`
      relative overflow-hidden rounded-2xl p-5 text-left transition-all
      duration-300 group w-full
      ${active
        ? 'ring-2 ring-offset-2 ring-teal-main shadow-lg scale-[1.02]'
        : 'hover:shadow-lg hover:-translate-y-0.5'
      }
    `}
  >
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-${active ? '100' : '90'}`} />
    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
    <div className="relative z-10 flex items-center justify-between">
      <div>
        <p className="text-3xl font-black text-white tracking-tight">{value}</p>
        <p className="text-sm font-semibold text-white/80 mt-1">{label}</p>
      </div>
      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white">
        {icon}
      </div>
    </div>
  </button>
);

// Rapat Card — desain modern
const RapatCard: React.FC<{ rapat: RapatPimpinan }> = ({ rapat }) => {
  const statusCfg = STATUS_CONFIG[rapat.status] || STATUS_CONFIG.dijadwalkan;
  const jenisCfg = JENIS_CONFIG[rapat.jenis] || JENIS_CONFIG.rutin;
  const tanggal = new Date(rapat.tanggal);
  const isUpcoming = rapat.status === 'dijadwalkan' && tanggal >= new Date();
  const isToday = tanggal.toDateString() === new Date().toDateString();

  return (
    <Link to={`/rapim/${rapat.id}`}>
      <div className={`
        group relative overflow-hidden rounded-2xl border bg-white
        transition-all duration-300 hover:shadow-xl hover:-translate-y-1
        ${isToday ? 'border-teal-main/30 ring-1 ring-teal-main/20' : 'border-neutral-border/50'}
      `}>
        {/* Accent top bar */}
        <div className={`h-1 bg-gradient-to-r ${statusCfg.gradient}`} />

        <div className="p-5">
          <div className="flex items-start gap-4">
            {/* Date Block */}
            <div className={`
              shrink-0 w-16 h-16 rounded-2xl flex flex-col items-center justify-center
              shadow-sm transition-transform group-hover:scale-105
              ${isToday
                ? 'bg-gradient-to-br from-teal-main to-teal-light text-white'
                : `${statusCfg.bg} ${statusCfg.color}`
              }
            `}>
              <span className="text-2xl font-black leading-none">
                {tanggal.getDate()}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider mt-0.5">
                {tanggal.toLocaleDateString('id-ID', { month: 'short' })}
              </span>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                {/* Jenis badge */}
                <span className="text-xs">{jenisCfg.emoji}</span>
                <span className={`
                  inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                  text-[10px] font-bold border ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}
                `}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                  {statusCfg.label}
                </span>
                {isToday && (
                  <span className="px-2 py-0.5 bg-teal-main text-white text-[10px] font-bold rounded-full animate-pulse">
                    HARI INI
                  </span>
                )}
                {isUpcoming && !isToday && (
                  <span className="text-[10px] text-blue-600 font-semibold">
                    {Math.ceil((tanggal.getTime() - Date.now()) / (1000 * 60 * 60 * 24))} hari lagi
                  </span>
                )}
              </div>

              <h3 className="text-base font-bold text-neutral-text-primary truncate group-hover:text-teal-main transition-colors">
                {rapat.judul}
              </h3>

              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-neutral-text-secondary">
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {tanggal.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })} WIB
                </span>
                {rapat.tempat && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="truncate max-w-[150px]">{rapat.tempat}</span>
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {(rapat as any).peserta_count ?? rapat.peserta?.length ?? 0} peserta
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" />
                  {(rapat as any).laporan_masalah_count ?? (rapat.laporan_masalah || rapat.laporan || []).length} laporan
                </span>
              </div>
            </div>

            {/* Arrow */}
            <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
              <div className="w-10 h-10 rounded-xl bg-teal-main/10 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-teal-main" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

// ========== MAIN COMPONENT ==========
const RapimCalendar: React.FC = () => {
  const { user } = useAuthStore();
  const { canScheduleRapim } = useCapabilities();

  // State
  const [view, setView] = useState<ViewMode>('list');
  const [rapatList, setRapatList] = useState<RapatPimpinan[]>([]);
  const [rapatCalendar, setRapatCalendar] = useState<RapatPimpinan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeStatus, setActiveStatus] = useState<StatusTab>('semua');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
  const [stats, setStats] = useState({
    total_rapat: 0, dijadwalkan: 0, selesai: 0,
    dibatalkan: 0, total_keputusan: 0, keputusan_selesai: 0, avg_progress: 0
  });

  // ========== HELPERS ==========
  const parsePaginatedResponse = (response: any) => {
    const payload = response?.data ?? response ?? {};
    const items = Array.isArray(payload?.data) ? payload.data
      : Array.isArray(payload) ? payload : [];
    const meta = {
      current_page: Number(payload?.current_page || payload?.meta?.current_page || 1),
      last_page: Number(payload?.last_page || payload?.meta?.last_page || 1),
      per_page: Number(payload?.per_page || payload?.meta?.per_page || 10),
      total: Number(payload?.total || payload?.meta?.total || items.length || 0)
    };
    return { items: items as RapatPimpinan[], meta };
  };

  // ========== FETCH ==========
  const fetchStats = async () => {
    const statsRes = await rapimService.getStats();
    setStats((statsRes as any)?.data || statsRes);
  };

  const fetchList = async (page: number, status: StatusTab, showLoader = true) => {
    if (showLoader) setLoadingList(true);
    try {
      const rapatRes = await rapimService.getAll({
        page, per_page: 10,
        status: status === 'semua' ? undefined : status
      });
      const { items, meta } = parsePaginatedResponse(rapatRes);
      setRapatList(items);
      setPagination(meta);
    } catch (err) {
      toast.error('Gagal memuat daftar RAPIM');
    } finally {
      if (showLoader) setLoadingList(false);
    }
  };

  const fetchCalendar = async (date: Date) => {
    try {
      const rapatRes = await rapimService.getAll({
        month: date.getMonth() + 1, year: date.getFullYear(),
        per_page: 200, page: 1
      });
      const { items } = parsePaginatedResponse(rapatRes);
      setRapatCalendar(items);
    } catch (err) {
      toast.error('Gagal memuat kalender');
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchList(1, 'semua', false), fetchCalendar(new Date())]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (view === 'calendar') fetchCalendar(currentMonth);
  }, [view, currentMonth]);

  const handleTabChange = (status: StatusTab) => {
    setActiveStatus(status);
    setCurrentPage(1);
    setSearchTerm('');
    fetchList(1, status, true);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > pagination.last_page) return;
    setCurrentPage(page);
    fetchList(page, activeStatus);
  };

  // ========== CALENDAR HELPERS ==========
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return {
      daysInMonth: new Date(year, month + 1, 0).getDate(),
      firstDayOfMonth: new Date(year, month, 1).getDay()
    };
  };

  const getRapatForDay = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    return rapatCalendar.filter((r) => {
      const d = new Date(r.tanggal);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    });
  };

  // Filter list by search
  const filteredList = useMemo(() => {
    if (!searchTerm) return rapatList;
    const s = searchTerm.toLowerCase();
    return rapatList.filter(r =>
      r.judul.toLowerCase().includes(s) ||
      (r.tempat || '').toLowerCase().includes(s)
    );
  }, [rapatList, searchTerm]);

  // ========== RENDER CALENDAR ==========
  const renderCalendar = () => {
    const { daysInMonth, firstDayOfMonth } = getDaysInMonth(currentMonth);
    const today = new Date();
    const cells = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push(<div key={`e-${i}`} className="h-24 rounded-xl bg-neutral-bg/30" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayRapat = getRapatForDay(day);
      const isToday = day === today.getDate()
        && currentMonth.getMonth() === today.getMonth()
        && currentMonth.getFullYear() === today.getFullYear();

      cells.push(
        <div
          key={day}
          className={`
            h-24 p-1.5 rounded-xl border transition-all group cursor-default
            ${isToday
              ? 'border-teal-main bg-teal-main/5 ring-1 ring-teal-main/20'
              : dayRapat.length > 0
                ? 'border-neutral-border bg-white hover:border-teal-light hover:shadow-sm'
                : 'border-neutral-border/30 bg-white'
            }
          `}
        >
          <div className={`
            text-xs font-bold mb-1 flex items-center justify-between
            ${isToday ? 'text-teal-main' : 'text-neutral-text-secondary'}
          `}>
            <span className={`
              ${isToday ? 'w-6 h-6 bg-teal-main text-white rounded-full flex items-center justify-center' : ''}
            `}>
              {day}
            </span>
            {dayRapat.length > 1 && (
              <span className="text-[9px] bg-neutral-bg rounded-full px-1.5 py-0.5">
                {dayRapat.length}
              </span>
            )}
          </div>
          <div className="space-y-0.5 overflow-hidden">
            {dayRapat.slice(0, 2).map((r) => {
              const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.dijadwalkan;
              return (
                <Link key={r.id} to={`/rapim/${r.id}`}>
                  <div className={`
                    text-[9px] px-1.5 py-0.5 rounded-md truncate font-medium
                    ${cfg.bg} ${cfg.color} hover:opacity-80 transition-opacity
                  `}>
                    {r.judul}
                  </div>
                </Link>
              );
            })}
            {dayRapat.length > 2 && (
              <div className="text-[9px] text-neutral-text-secondary px-1 font-medium">
                +{dayRapat.length - 2} lagi
              </div>
            )}
          </div>
        </div>
      );
    }
    return cells;
  };

  // ========== LOADING ==========
  if (loading) {
    return <Loading variant="page" message="Memuat jadwal rapat..." />;
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Rapat Pimpinan' },
      ]} />

      {/* ===== HERO HEADER ===== */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-dark via-[#0a1628] to-teal-main p-6 md:p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-teal-light/10 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Gavel className="w-6 h-6 text-teal-light" />
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-light/80">
                Rapat Pimpinan
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
              RAPIM
            </h1>
            <p className="text-sm text-white/60 mt-1">
              Kelola jadwal, agenda, dan notulensi rapat pimpinan
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* View Toggle — Pill Style */}
            <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-1 border border-white/10">
              <button
                onClick={() => setView('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'list'
                  ? 'bg-white text-teal-dark shadow-sm'
                  : 'text-white/70 hover:text-white'
                  }`}
              >
                <List className="w-4 h-4" /> List
              </button>
              <button
                onClick={() => setView('calendar')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'calendar'
                  ? 'bg-white text-teal-dark shadow-sm'
                  : 'text-white/70 hover:text-white'
                  }`}
              >
                <CalendarDays className="w-4 h-4" /> Kalender
              </button>
            </div>

            {canScheduleRapim && (
              <Link to="/rapim/buat">
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Plus className="w-4 h-4" />}
                  className="!bg-white !text-teal-dark hover:!bg-teal-light hover:!text-white shadow-lg"
                >
                  Buat Rapat
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ===== STAT CARDS ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Calendar className="w-6 h-6" />}
          label="Dijadwalkan"
          value={stats.dijadwalkan}
          gradient="from-blue-500 to-blue-600"
          active={activeStatus === 'dijadwalkan'}
          onClick={() => { setView('list'); handleTabChange('dijadwalkan'); }}
        />
        <StatCard
          icon={<Play className="w-6 h-6" />}
          label="Berlangsung"
          value={stats.total_rapat - stats.dijadwalkan - stats.selesai - stats.dibatalkan}
          gradient="from-amber-500 to-amber-600"
          active={activeStatus === 'berlangsung'}
          onClick={() => { setView('list'); handleTabChange('berlangsung'); }}
        />
        <StatCard
          icon={<CheckCircle className="w-6 h-6" />}
          label="Selesai"
          value={stats.selesai}
          gradient="from-emerald-500 to-emerald-600"
          active={activeStatus === 'selesai'}
          onClick={() => { setView('list'); handleTabChange('selesai'); }}
        />
        <StatCard
          icon={<Gavel className="w-6 h-6" />}
          label="Total Keputusan"
          value={stats.total_keputusan}
          gradient="from-purple-500 to-purple-600"
        />
      </div>

      {/* ===== LIST VIEW ===== */}
      {view === 'list' && (
        <div className="space-y-4">
          {/* Search + Filter Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
              <input
                type="text"
                placeholder="Cari rapat berdasarkan judul atau tempat..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-3 rounded-xl border-2 border-neutral-border bg-white text-sm
                  focus:outline-none focus:border-teal-main focus:ring-0 transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-bg rounded-lg"
                >
                  <X className="w-4 h-4 text-neutral-text-secondary" />
                </button>
              )}
            </div>

            {/* Status Tabs — Pill */}
            <div className="flex items-center gap-1 p-1 bg-neutral-bg rounded-xl overflow-x-auto">
              {[
                { key: 'semua' as StatusTab, label: 'Semua', count: stats.total_rapat },
                { key: 'dijadwalkan' as StatusTab, label: 'Jadwal', count: stats.dijadwalkan },
                { key: 'selesai' as StatusTab, label: 'Selesai', count: stats.selesai },
                { key: 'dibatalkan' as StatusTab, label: 'Batal', count: stats.dibatalkan },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold
                    whitespace-nowrap transition-all
                    ${activeStatus === tab.key
                      ? 'bg-white text-teal-main shadow-sm'
                      : 'text-neutral-text-secondary hover:text-neutral-text-primary'
                    }
                  `}
                >
                  {tab.label}
                  <span className={`
                    min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold
                    flex items-center justify-center
                    ${activeStatus === tab.key
                      ? 'bg-teal-main/10 text-teal-main'
                      : 'bg-neutral-border/50'
                    }
                  `}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Rapat List */}
          {loadingList ? (
            <Loading variant="inline" message="Memuat daftar rapat..." size="md" />
          ) : filteredList.length > 0 ? (
            <div className="space-y-3">
              {filteredList.map((r) => (
                <RapatCard key={r.id} rapat={r} />
              ))}
            </div>
          ) : (
            <Card className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-neutral-bg flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-10 h-10 text-neutral-text-secondary opacity-30" />
              </div>
              <p className="text-lg font-bold text-neutral-text-primary">
                {searchTerm ? 'Tidak ditemukan' : 'Belum ada rapat'}
              </p>
              <p className="text-sm text-neutral-text-secondary mt-1 max-w-sm mx-auto">
                {searchTerm
                  ? `Tidak ada rapat yang cocok dengan "${searchTerm}"`
                  : 'Jadwalkan rapat pimpinan pertama untuk memulai.'
                }
              </p>
              {searchTerm && (
                <Button variant="ghost" size="sm" className="mt-3" onClick={() => setSearchTerm('')}>
                  Hapus Pencarian
                </Button>
              )}
            </Card>
          )}

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="flex items-center justify-between px-2">
              <p className="text-xs text-neutral-text-secondary">
                {pagination.total} rapat total
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="p-2 rounded-lg hover:bg-neutral-bg disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  let page: number;
                  if (pagination.last_page <= 5) page = i + 1;
                  else if (currentPage <= 3) page = i + 1;
                  else if (currentPage >= pagination.last_page - 2) page = pagination.last_page - 4 + i;
                  else page = currentPage - 2 + i;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-bold transition-colors ${page === currentPage
                        ? 'bg-teal-main text-white shadow-sm'
                        : 'hover:bg-neutral-bg text-neutral-text-secondary'
                        }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= pagination.last_page}
                  className="p-2 rounded-lg hover:bg-neutral-bg disabled:opacity-30 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== CALENDAR VIEW ===== */}
      {view === 'calendar' && (
        <Card className="!p-0 overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-border bg-gradient-to-r from-neutral-bg to-white">
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h3 className="text-lg font-black text-teal-dark min-w-[200px] text-center">
                {NAMA_BULAN[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
              Hari Ini
            </Button>
          </div>

          {/* Calendar Body */}
          <div className="p-4">
            {/* Day Names */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {NAMA_HARI.map((hari) => (
                <div key={hari} className="text-center text-xs font-bold text-neutral-text-secondary py-2 uppercase tracking-wider">
                  {hari}
                </div>
              ))}
            </div>
            {/* Day Cells */}
            <div className="grid grid-cols-7 gap-2">
              {renderCalendar()}
            </div>
          </div>

          {/* Legend */}
          <div className="px-4 py-3 border-t border-neutral-border bg-neutral-bg/50">
            <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-text-secondary">
              {Object.entries(JENIS_CONFIG).map(([key, cfg]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <span>{cfg.emoji}</span>
                  <span>{cfg.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default RapimCalendar;
